import { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, ExternalLink } from '@digital-twins/design-system/icons'

import { cn } from '@/lib/utils'

import { CitationImageView } from './CitationImageView'
import { CitationSource, compactCitationDomain } from './citationSource'
import { citationVerificationCopy } from './citationVerification'
import { formatFigureLine } from './formatFigure'
import { SourceIcon } from './SourceIcon'

export interface SourceHoverCardProps {
  sources: Array<CitationSource>
  index: number
  onIndexChange: (index: number) => void
  testId: string
  /** Shown as an "Open source" button; the touch path, where a tap only opens the card. */
  onOpenSource?: () => void
}

const NAV_BUTTON_CLASS =
  'inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30 disabled:opacity-40'

/**
 * The card body behind a source pill: the source's own picture when it has
 * one (a social preview carries its own), icon, name, location, then whatever the
 * source itself provided (its text, the cited figures, or a social preview)
 * in one muted quote block. Arrows page through a grouped pill's sources.
 */
export function SourceHoverCard(props: SourceHoverCardProps) {
  const { sources, index, onIndexChange, testId, onOpenSource } = props

  const [failedImageKey, setFailedImageKey] = useState<string | null>(null)

  const { t, i18n } = useTranslation('app')

  const total = sources.length
  const safeIndex = Math.min(Math.max(index, 0), Math.max(total - 1, 0))
  const source = sources[safeIndex]

  const onPrevious = () => {
    onIndexChange((safeIndex - 1 + total) % total)
  }
  const onNext = () => {
    onIndexChange((safeIndex + 1) % total)
  }

  if (!source) {
    return null
  }

  const figureLines = (source.figures ?? []).map((figure) => formatFigureLine(figure, t, i18n.language))
  // The social preview already shows the post's picture: never both.
  const sourceImage = source.image && !source.preview && failedImageKey !== source.key ? source.image : null
  const headerImage = sourceImage ? (
    <CitationImageView
      key={source.key}
      image={sourceImage}
      alt={source.kind === 'web' ? compactCitationDomain(source.name) : source.name}
      className="h-28 w-full rounded object-cover"
      testId="citation-hover-card-image"
      onError={() => setFailedImageKey(source.key)}
    />
  ) : null
  let body: ReactNode = null
  if (source.preview) {
    body = source.preview
  } else if (source.quote) {
    body = <span className="line-clamp-6 whitespace-normal">{source.quote}</span>
  } else if (figureLines.length > 0) {
    body = (
      <ul className="space-y-0.5">
        {figureLines.map((line, lineIndex) => (
          <li key={`${source.key}-figure-${lineIndex}`} data-testid={`${testId}-figure-${lineIndex}`}>
            {line}
          </li>
        ))}
      </ul>
    )
  }
  const quote = body ? (
    <blockquote
      className="border-l-2 border-muted-foreground/30 bg-muted/40 px-2 py-1 text-sm text-foreground"
      data-testid={`${testId}-quote`}
    >
      {body}
    </blockquote>
  ) : null
  const hint = source.verification === 'matched' ? null : (
    <p className="text-xs text-muted-foreground" data-testid={`${testId}-hint`}>
      {citationVerificationCopy(source.verification).description}
    </p>
  )
  const nav = total > 1 ? (
    <div className="flex items-center justify-end gap-1 pt-0.5">
      <button
        type="button"
        className={NAV_BUTTON_CLASS}
        aria-label={t('threads.detail.sourcePill.previous')}
        data-testid={`${testId}-prev`}
        onClick={onPrevious}
      >
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
      </button>
      <span className="text-xs tabular-nums text-muted-foreground" aria-live="polite" data-testid={`${testId}-position`}>
        {t('threads.detail.sourcePill.position', { current: safeIndex + 1, total })}
      </span>
      <button
        type="button"
        className={NAV_BUTTON_CLASS}
        aria-label={t('threads.detail.sourcePill.next')}
        data-testid={`${testId}-next`}
        onClick={onNext}
      >
        <ChevronRight className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  ) : null
  const recordLink = source.href ? (
    <a
      href={source.href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`${testId}-record-link`}
      className="inline-flex h-6 items-center text-xs font-medium text-link underline underline-offset-2 hover:text-link-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30"
    >
      {t('threads.detail.sourcePill.openLink')}
    </a>
  ) : null
  const openButton = onOpenSource ? (
    <button
      type="button"
      className={cn(
        'inline-flex h-6 items-center gap-1 rounded px-1 text-xs font-medium text-foreground underline-offset-2 hover:underline',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/30',
      )}
      data-testid={`${testId}-open`}
      onClick={onOpenSource}
    >
      <ExternalLink className="h-3 w-3" aria-hidden />
      {t('threads.detail.sourcePill.open')}
    </button>
  ) : null

  return (
    <div className="space-y-1.5 break-words" data-testid={testId} data-source-kind={source.kind}>
      {headerImage}
      <div className="flex items-start gap-2">
        <SourceIcon source={source} className="mt-0.5 h-3.5 w-3.5" />
        <div className="min-w-0 flex-1">
          <p className="line-clamp-2 text-xs font-medium text-foreground" data-testid={`${testId}-name`}>
            {source.kind === 'web' ? compactCitationDomain(source.name) : source.name}
          </p>
          {source.location ? (
            <p className="text-xs text-muted-foreground" data-testid={`${testId}-location`}>
              {source.kind === 'web' ? compactCitationDomain(source.location) : source.location}
            </p>
          ) : null}
        </div>
      </div>
      {quote}
      {hint}
      {nav}
      {recordLink}
      {openButton}
    </div>
  )
}
